/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { CoreNetworking } from '@airasia-rn/ui';
import {
  NearestAirportApiResponse,
  NearestAirportsCodesResponse,
} from '../types/nearestAirportsCodes.types';
import EnvironmentURLs, { parseEnv } from '../util/EnvironmentURLs';

declare const __DEV__: boolean;

export type Environment = 'stg' | 'prd';

const mapNearestAirportResponse = (
  apiResponse: NearestAirportApiResponse,
): NearestAirportsCodesResponse => {
  const data = apiResponse?.data;
  const airports = data?.airports ?? [];
  const latitude = data?.latitude;
  const longitude = data?.longitude;
  const hasCoordinates =
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude);

  return {
    country: data?.country ?? '',
    countryName: data?.countryName ?? '',
    city: data?.city ?? '',
    airport: airports.map((airport) => airport.stationCode),
    latlong: hasCoordinates ? `${latitude},${longitude}` : undefined,
    locKey: data?.locKey ?? '',
    airports,
  };
};

export class NearestAirportsCodesApiService {
  constructor(private environment: Environment) {}
  private getNearestAirportsUrl(): string {
    return EnvironmentURLs.NEAREST_AIRPORTS[parseEnv(this.environment)];
  }
  async fetchNearestAirports(
    locale?: string,
    signal?: AbortSignal,
  ): Promise<NearestAirportsCodesResponse> {
    try {
      const url = this.getNearestAirportsUrl();
      const query = locale
        ? `?${new URLSearchParams({ locale }).toString()}`
        : '';
      const response = await CoreNetworking.get(`${url}${query}`, {
        signal,
      });

      return mapNearestAirportResponse(
        response?.data as NearestAirportApiResponse,
      );
    } catch (err) {
      if (__DEV__) {
        console.log('❌ [NearestAirportApi] Fetch ERROR:', err);
      }
      throw err;
    }
  }
}
